import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  AlignLeft, AlignCenter, AlignRight, AlignJustify,
  X, Paintbrush, Send, Loader2, ChevronDown, Maximize2, ImageIcon, Upload, Link, ExternalLink, Globe,
  Trash2, Copy, Box
} from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import AnimationPanel from './AnimationPanel';
import { ColorPicker } from '../ui/color-picker';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ─── Helpers ───

function rgbToHex(rgb) {
  if (!rgb || rgb === 'rgba(0, 0, 0, 0)' || rgb === 'transparent') return '';
  const match = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!match) return rgb;
  return '#' + [match[1], match[2], match[3]].map(n => parseInt(n).toString(16).padStart(2, '0')).join('');
}

function parsePx(val) {
  if (!val) return '';
  return parseFloat(val) || '';
}

// Shadow presets: label → CSS value
const SHADOW_PRESETS = [
  { label: 'None', value: 'none' },
  { label: 'Small', value: '0 1px 2px 0 rgb(0 0 0 / 0.05)' },
  { label: 'Default', value: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)' },
  { label: 'Medium', value: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' },
  { label: 'Large', value: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)' },
  { label: 'XL', value: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)' },
  { label: '2XL', value: '0 25px 50px -12px rgb(0 0 0 / 0.25)' },
  { label: '3XL', value: '0 35px 60px -15px rgb(0 0 0 / 0.3)' },
  { label: 'Full', value: '0 0 0 9999px rgb(0 0 0 / 0.5)' },
  { label: 'Inner', value: 'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)' },
];

// Border radius presets: label → px value (used in CSS)
const RADIUS_PRESETS = [
  { label: 'None', value: '0' },
  { label: 'Small', value: '4' },
  { label: 'Default', value: '8' },
  { label: 'Medium', value: '12' },
  { label: 'Large', value: '16' },
  { label: 'XL', value: '24' },
  { label: '2XL', value: '32' },
  { label: '3XL', value: '48' },
  { label: 'Full', value: '9999' },
];

// Border width presets
const BORDER_WIDTH_PRESETS = [
  { label: 'None', value: '0' },
  { label: '1px', value: '1' },
  { label: '2px', value: '2' },
  { label: '3px', value: '3' },
  { label: '4px', value: '4' },
  { label: '8px', value: '8' },
];

// Opacity presets (percentage string → number 0–100)
const OPACITY_PRESETS = [
  { label: '100%', value: 100 },
  { label: '90%', value: 90 },
  { label: '75%', value: 75 },
  { label: '50%', value: 50 },
  { label: '25%', value: 25 },
  { label: '10%', value: 10 },
  { label: '0%', value: 0 },
];

// ─── Collapsible Section ───

function Section({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[rgba(255,255,255,0.07)]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-normal text-[var(--orbis-t3)] uppercase tracking-wider hover:text-[var(--orbis-t1)] transition-colors"
      >
        {title}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-4 pb-3 space-y-2.5">{children}</div>}
    </div>
  );
}

// ─── Input Components ───

function FieldLabel({ children }) {
  return <label className="text-[11px] text-[var(--orbis-t4)] block mb-1">{children}</label>;
}

function TextInput({ label, value, onChange, placeholder }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none focus:border-[rgba(255,255,255,0.12)]"
      />
    </div>
  );
}

function ColorInput({ label, value, onChange, allowGradient = false }) {
  const isEmpty = !value;
  const isGradient = value && (value.startsWith('linear-gradient') || value.startsWith('radial-gradient'));
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <ColorPicker
        value={value || '#000000'}
        onValueChange={onChange}
        mode={allowGradient ? 'any' : 'solid'}
      >
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 rounded-md border border-[rgba(255,255,255,0.12)] overflow-hidden shrink-0">
          {isEmpty ? (
            <div className="w-full h-full flex">
              <div className="w-1/2 h-full bg-white" />
              <div className="w-1/2 h-full bg-[var(--orbis-s1)]" />
            </div>
          ) : (
            <div className="w-full h-full" style={{ background: value }} />
          )}
        </div>
        <span className="flex-1 text-xs font-mono text-[var(--orbis-t2)] truncate">
          {isEmpty ? 'Inherit' : (isGradient ? 'gradient' : value)}
        </span>
      </div>
      </ColorPicker>
    </div>
  );
}

function SelectInput({ label, value, onChange, options }) {
  return (
    <div>
      {label && <FieldLabel>{label}</FieldLabel>}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] focus:outline-none focus:border-[rgba(255,255,255,0.12)]"
      >
        {options.map(o => (
          <option key={o.value ?? o.label} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Compact Spacing (H+V with expand) ───

function CompactSpacing({ label, top, right, bottom, left, onChange }) {
  const [expanded, setExpanded] = useState(false);

  // Derived horizontal / vertical averages for the compact view
  const hVal = right !== '' && left !== '' ? (parseFloat(right) === parseFloat(left) ? right : '') : (right || left || '');
  const vVal = top !== '' && bottom !== '' ? (parseFloat(top) === parseFloat(bottom) ? top : '') : (top || bottom || '');

  const setH = (v) => { onChange('left', v); onChange('right', v); };
  const setV = (v) => { onChange('top', v); onChange('bottom', v); };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <FieldLabel>{label}</FieldLabel>
        <button
          onClick={() => setExpanded(e => !e)}
          className={`p-0.5 rounded transition-colors ${expanded ? 'text-[var(--orbis-amber)]' : 'text-[var(--orbis-t4)] hover:text-[var(--orbis-t3)]'}`}
          title={expanded ? 'Collapse' : 'Expand all sides'}
        >
          <Maximize2 className="w-3 h-3" />
        </button>
      </div>

      {!expanded ? (
        <div className="grid grid-cols-2 gap-2">
          {[
            { key: 'H', val: hVal, set: setH, title: 'Horizontal (left + right)' },
            { key: 'V', val: vVal, set: setV, title: 'Vertical (top + bottom)' },
          ].map(s => (
            <div key={s.key} className="relative">
              <input
                type="number"
                value={s.val}
                onChange={e => s.set(e.target.value)}
                min={0}
                title={s.title}
                placeholder="—"
                className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2 py-1 text-[11px] text-[var(--orbis-t1)] text-center focus:outline-none focus:border-[rgba(255,255,255,0.12)] placeholder:text-[var(--orbis-t4)]"
              />
              <span className="absolute top-0.5 left-1.5 text-[8px] text-[var(--orbis-t4)]">{s.key}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1">
          {[
            { key: 'top', val: top, lbl: 'T' },
            { key: 'right', val: right, lbl: 'R' },
            { key: 'bottom', val: bottom, lbl: 'B' },
            { key: 'left', val: left, lbl: 'L' },
          ].map(s => (
            <div key={s.key} className="relative">
              <input
                type="number"
                value={s.val}
                onChange={e => onChange(s.key, e.target.value)}
                min={0}
                className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-1.5 py-1 text-[11px] text-[var(--orbis-t1)] text-center focus:outline-none focus:border-[rgba(255,255,255,0.12)]"
              />
              <span className="absolute top-0.5 left-1 text-[8px] text-[var(--orbis-t4)]">{s.lbl}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// CSS state options
const CSS_STATES = [
  { value: 'default', label: 'Default' },
  { value: 'hover', label: ':hover' },
  { value: 'active', label: ':active' },
  { value: 'focus', label: ':focus' },
];

// ─── Main Component ───

export default function VisualEditPanel({
  selectedElement,
  onApplyEdit,
  onLinkChange,
  isApplying,
  iframeRef,
  onClose,
  projectId,
  currentPageSlug,
  onEdit,
  reviewEditorState,
  onAnimationApplied,
}) {
  const cs = selectedElement?.computedStyles || {};

  // Local state
  const [content, setContent] = useState('');
  const [fontSize, setFontSize] = useState('');
  const [fontWeight, setFontWeight] = useState('');
  const [fontStyle, setFontStyle] = useState('normal');
  const [textAlign, setTextAlign] = useState('left');
  const [textColor, setTextColor] = useState('');
  const [bgColor, setBgColor] = useState('');
  const [paddingTop, setPaddingTop] = useState('');
  const [paddingRight, setPaddingRight] = useState('');
  const [paddingBottom, setPaddingBottom] = useState('');
  const [paddingLeft, setPaddingLeft] = useState('');
  const [marginTop, setMarginTop] = useState('');
  const [marginRight, setMarginRight] = useState('');
  const [marginBottom, setMarginBottom] = useState('');
  const [marginLeft, setMarginLeft] = useState('');
  const [borderWidth, setBorderWidth] = useState('0');
  const [borderColor, setBorderColor] = useState('');
  const [borderStyle, setBorderStyle] = useState('none');
  const [borderRadius, setBorderRadius] = useState('0');  // px value string
  const [opacity, setOpacity] = useState(100);            // 0–100 number
  const [boxShadow, setBoxShadow] = useState('none');     // raw CSS string
  const [editRequest, setEditRequest] = useState('');
  const [cssState, setCssState] = useState('default');

  // Text extras
  const [lineHeight, setLineHeight] = useState('');
  const [letterSpacing, setLetterSpacing] = useState('');
  const [textTransform, setTextTransform] = useState('none');
  const [textDecoration, setTextDecoration] = useState('none');

  // Sizing
  const [elWidth, setElWidth] = useState('');
  const [elHeight, setElHeight] = useState('');
  const [minWidth, setMinWidth] = useState('');
  const [maxWidth, setMaxWidth] = useState('');
  const [minHeight, setMinHeight] = useState('');
  const [maxHeight, setMaxHeight] = useState('');

  // Layout
  const [display, setDisplay] = useState('');
  const [flexDirection, setFlexDirection] = useState('row');
  const [justifyContent, setJustifyContent] = useState('flex-start');
  const [alignItems, setAlignItems] = useState('stretch');
  const [gap, setGap] = useState('');
  const [flexWrap, setFlexWrap] = useState('nowrap');

  // Position
  const [position, setPosition] = useState('static');
  const [posTop, setPosTop] = useState('');
  const [posLeft, setPosLeft] = useState('');
  const [posRight, setPosRight] = useState('');
  const [posBottom, setPosBottom] = useState('');
  const [zIndex, setZIndex] = useState('');

  // Advanced effects
  const [filterBlur, setFilterBlur] = useState(0);
  const [filterBrightness, setFilterBrightness] = useState(100);
  const [filterContrast, setFilterContrast] = useState(100);
  const [filterGrayscale, setFilterGrayscale] = useState(0);
  const [mixBlendMode, setMixBlendMode] = useState('normal');
  const [cursor, setCursorStyle] = useState('auto');
  const [overflow, setOverflow] = useState('visible');

  const [originals, setOriginals] = useState({});
  // Track which properties the user explicitly changed via UI controls (Bug 4 fix).
  // Reset when selecting a new element. Only properties in this set get saved.
  const [touchedProps, setTouchedProps] = useState(new Set());
  const markTouched = useCallback((...props) => setTouchedProps(prev => {
    const next = new Set(prev);
    props.forEach(p => next.add(p));
    return next;
  }), []);
  // Auto-mark: wrap any setter call from a UI handler with touchSetter
  const touchSetter = useCallback((prop, setter) => (val) => {
    markTouched(prop);
    setter(val);
  }, [markTouched]);

  // Image replacement state (only used when selectedElement is <img>)
  const [newImageSrc, setNewImageSrc] = useState('');
  const [newImagePreview, setNewImagePreview] = useState('');
  const fileInputRef = useRef(null);

  // Link editing state (for <a> elements or elements inside <a>)
  const [linkHref, setLinkHref] = useState('');
  const [linkNewTab, setLinkNewTab] = useState(false);

  const getDirectText = useCallback((element) => {
    if (!element) return '';
    const text = element.textContent || '';
    return text.length > 100 ? text.substring(0, 100) : text;
  }, []);

  // Match a computed px value to the nearest preset label value
  const matchRadiusPreset = (pxVal) => {
    if (!pxVal && pxVal !== 0) return '0';
    const n = parseFloat(pxVal);
    const closest = RADIUS_PRESETS.reduce((a, b) =>
      Math.abs(parseFloat(b.value) - n) < Math.abs(parseFloat(a.value) - n) ? b : a
    );
    return closest.value;
  };

  const matchBorderWidthPreset = (pxVal) => {
    if (!pxVal && pxVal !== 0) return '0';
    const n = parseFloat(pxVal);
    const closest = BORDER_WIDTH_PRESETS.reduce((a, b) =>
      Math.abs(parseFloat(b.value) - n) < Math.abs(parseFloat(a.value) - n) ? b : a
    );
    return closest.value;
  };

  const matchShadowPreset = (cssVal) => {
    if (!cssVal || cssVal === 'none') return 'none';
    // Try exact match first
    const exact = SHADOW_PRESETS.find(p => p.value === cssVal);
    if (exact) return exact.value;
    // Inset heuristic
    if (cssVal.startsWith('inset')) return SHADOW_PRESETS.find(p => p.label === 'Inner').value;
    return 'none';
  };

  // Populate from selected element
  useEffect(() => {
    if (!selectedElement) return;
    const s = selectedElement.computedStyles || {};
    const vals = {
      content: getDirectText(selectedElement),
      fontSize: parsePx(s.fontSize),
      fontWeight: s.fontWeight || '400',
      fontStyle: s.fontStyle || 'normal',
      textAlign: s.textAlign || 'left',
      textColor: rgbToHex(s.color),
      bgColor: rgbToHex(s.backgroundColor),
      paddingTop: parsePx(s.paddingTop),
      paddingRight: parsePx(s.paddingRight),
      paddingBottom: parsePx(s.paddingBottom),
      paddingLeft: parsePx(s.paddingLeft),
      marginTop: parsePx(s.marginTop),
      marginRight: parsePx(s.marginRight),
      marginBottom: parsePx(s.marginBottom),
      marginLeft: parsePx(s.marginLeft),
      borderWidth: matchBorderWidthPreset(parsePx(s.borderWidth)),
      borderColor: rgbToHex(s.borderColor),
      borderStyle: s.borderStyle || 'none',
      borderRadius: matchRadiusPreset(parsePx(s.borderRadius)),
      opacity: s.opacity ? Math.round(parseFloat(s.opacity) * 100) : 100,
      boxShadow: matchShadowPreset(s.boxShadow === 'none' ? 'none' : s.boxShadow || 'none'),
      // Text extras
      lineHeight: s.lineHeight && s.lineHeight !== 'normal' ? parseFloat(s.lineHeight) || '' : '',
      letterSpacing: parsePx(s.letterSpacing) || 0,
      textTransform: s.textTransform || 'none',
      textDecoration: s.textDecorationLine || s.textDecoration || 'none',
      // Sizing
      elWidth: s.width && s.width !== 'auto' ? parsePx(s.width) : '',
      elHeight: s.height && s.height !== 'auto' ? parsePx(s.height) : '',
      minWidth: s.minWidth && s.minWidth !== 'none' && s.minWidth !== '0px' ? parsePx(s.minWidth) : '',
      maxWidth: s.maxWidth && s.maxWidth !== 'none' ? parsePx(s.maxWidth) : '',
      minHeight: s.minHeight && s.minHeight !== 'none' && s.minHeight !== '0px' ? parsePx(s.minHeight) : '',
      maxHeight: s.maxHeight && s.maxHeight !== 'none' ? parsePx(s.maxHeight) : '',
      // Layout
      display: s.display || '',
      flexDirection: s.flexDirection || 'row',
      justifyContent: s.justifyContent || 'flex-start',
      alignItems: s.alignItems || 'stretch',
      gap: s.gap && s.gap !== 'normal' ? parsePx(s.gap) : '',
      flexWrap: s.flexWrap || 'nowrap',
      // Position
      position: s.position || 'static',
      posTop: s.top && s.top !== 'auto' ? parsePx(s.top) : '',
      posLeft: s.left && s.left !== 'auto' ? parsePx(s.left) : '',
      posRight: s.right && s.right !== 'auto' ? parsePx(s.right) : '',
      posBottom: s.bottom && s.bottom !== 'auto' ? parsePx(s.bottom) : '',
      zIndex: s.zIndex && s.zIndex !== 'auto' ? s.zIndex : '',
      // Advanced effects
      mixBlendMode: s.mixBlendMode || 'normal',
      cursor: s.cursor || 'auto',
      overflow: s.overflow || 'visible',
    };
    setOriginals(vals);
    setTouchedProps(new Set()); // Reset touched tracking for new element
    setContent(vals.content);
    setFontSize(vals.fontSize);
    setFontWeight(vals.fontWeight);
    setFontStyle(vals.fontStyle);
    setTextAlign(vals.textAlign);
    setTextColor(vals.textColor);
    setBgColor(vals.bgColor);
    setPaddingTop(vals.paddingTop);
    setPaddingRight(vals.paddingRight);
    setPaddingBottom(vals.paddingBottom);
    setPaddingLeft(vals.paddingLeft);
    setMarginTop(vals.marginTop);
    setMarginRight(vals.marginRight);
    setMarginBottom(vals.marginBottom);
    setMarginLeft(vals.marginLeft);
    setBorderWidth(vals.borderWidth);
    setBorderColor(vals.borderColor);
    setBorderStyle(vals.borderStyle);
    setBorderRadius(vals.borderRadius);
    setOpacity(vals.opacity);
    setBoxShadow(vals.boxShadow);

    // Text extras
    setLineHeight(s.lineHeight && s.lineHeight !== 'normal' ? parseFloat(s.lineHeight) || '' : '');
    setLetterSpacing(parsePx(s.letterSpacing) || 0);
    setTextTransform(s.textTransform || 'none');
    setTextDecoration(s.textDecorationLine || s.textDecoration || 'none');

    // Sizing
    setElWidth(s.width && s.width !== 'auto' ? parsePx(s.width) : '');
    setElHeight(s.height && s.height !== 'auto' ? parsePx(s.height) : '');
    setMinWidth(s.minWidth && s.minWidth !== 'none' && s.minWidth !== '0px' ? parsePx(s.minWidth) : '');
    setMaxWidth(s.maxWidth && s.maxWidth !== 'none' ? parsePx(s.maxWidth) : '');
    setMinHeight(s.minHeight && s.minHeight !== 'none' && s.minHeight !== '0px' ? parsePx(s.minHeight) : '');
    setMaxHeight(s.maxHeight && s.maxHeight !== 'none' ? parsePx(s.maxHeight) : '');

    // Layout
    setDisplay(s.display || '');
    setFlexDirection(s.flexDirection || 'row');
    setJustifyContent(s.justifyContent || 'flex-start');
    setAlignItems(s.alignItems || 'stretch');
    setGap(s.gap && s.gap !== 'normal' ? parsePx(s.gap) : '');
    setFlexWrap(s.flexWrap || 'nowrap');

    // Position
    setPosition(s.position || 'static');
    setPosTop(s.top && s.top !== 'auto' ? parsePx(s.top) : '');
    setPosLeft(s.left && s.left !== 'auto' ? parsePx(s.left) : '');
    setPosRight(s.right && s.right !== 'auto' ? parsePx(s.right) : '');
    setPosBottom(s.bottom && s.bottom !== 'auto' ? parsePx(s.bottom) : '');
    setZIndex(s.zIndex && s.zIndex !== 'auto' ? s.zIndex : '');

    // Advanced effects — parse filter string
    const filterStr = s.filter || 'none';
    const blurMatch = filterStr.match(/blur\((\d+(?:\.\d+)?)px\)/);
    const brightnessMatch = filterStr.match(/brightness\((\d+(?:\.\d+)?)\)/);
    const contrastMatch = filterStr.match(/contrast\((\d+(?:\.\d+)?)\)/);
    const grayscaleMatch = filterStr.match(/grayscale\((\d+(?:\.\d+)?)\)/);
    setFilterBlur(blurMatch ? parseFloat(blurMatch[1]) : 0);
    setFilterBrightness(brightnessMatch ? Math.round(parseFloat(brightnessMatch[1]) * 100) : 100);
    setFilterContrast(contrastMatch ? Math.round(parseFloat(contrastMatch[1]) * 100) : 100);
    setFilterGrayscale(grayscaleMatch ? Math.round(parseFloat(grayscaleMatch[1]) * 100) : 0);
    setMixBlendMode(s.mixBlendMode || 'normal');
    setCursorStyle(s.cursor || 'auto');
    setOverflow(s.overflow || 'visible');

    setEditRequest('');
    setCssState('default');
    setNewImageSrc('');
    setNewImagePreview('');
    // Initialize link state
    setLinkHref(selectedElement?.href || '');
    setLinkNewTab(false);
  }, [selectedElement, getDirectText]);

  // Correct originals when mouse leaves iframe (captures true non-hover defaults)
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type !== 'wf_default_styles') return;
      const s = e.data.computedStyles || {};
      const vals = {
        content: originals.content || '',
        fontSize: parsePx(s.fontSize),
        fontWeight: s.fontWeight || '400',
        fontStyle: s.fontStyle || 'normal',
        textAlign: s.textAlign || 'left',
        textColor: rgbToHex(s.color),
        bgColor: rgbToHex(s.backgroundColor),
        paddingTop: parsePx(s.paddingTop),
        paddingRight: parsePx(s.paddingRight),
        paddingBottom: parsePx(s.paddingBottom),
        paddingLeft: parsePx(s.paddingLeft),
        marginTop: parsePx(s.marginTop),
        marginRight: parsePx(s.marginRight),
        marginBottom: parsePx(s.marginBottom),
        marginLeft: parsePx(s.marginLeft),
        borderWidth: matchBorderWidthPreset(parsePx(s.borderWidth)),
        borderColor: rgbToHex(s.borderColor),
        borderStyle: s.borderStyle || 'none',
        borderRadius: matchRadiusPreset(parsePx(s.borderRadius)),
        opacity: s.opacity ? Math.round(parseFloat(s.opacity) * 100) : 100,
        boxShadow: matchShadowPreset(s.boxShadow === 'none' ? 'none' : s.boxShadow || 'none'),
      };
      setOriginals(vals);
      // Update displayed values if still on default state
      setTextColor(v => v === originals.textColor ? vals.textColor : v);
      setBgColor(v => v === originals.bgColor ? vals.bgColor : v);
      setBorderColor(v => v === originals.borderColor ? vals.borderColor : v);
      setBorderWidth(v => v === originals.borderWidth ? vals.borderWidth : v);
      setBorderRadius(v => v === originals.borderRadius ? vals.borderRadius : v);
      setFontSize(v => String(v) === String(originals.fontSize) ? vals.fontSize : v);
      setFontWeight(v => v === originals.fontWeight ? vals.fontWeight : v);
      setFontStyle(v => v === originals.fontStyle ? vals.fontStyle : v);
      setOpacity(v => v === originals.opacity ? vals.opacity : v);
      setBoxShadow(v => v === originals.boxShadow ? vals.boxShadow : v);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [originals]);

  // Merged cssState effect: always reset to originals first, then overlay pseudo values
  useEffect(() => {
    if (!originals || Object.keys(originals).length === 0) return;

    // Step 1: always restore originals (clears bleed-through from other pseudo-states)
    setTextColor(originals.textColor || '');
    setBgColor(originals.bgColor || '');
    setBorderColor(originals.borderColor || '');
    setBorderWidth(originals.borderWidth || '0');
    setBorderRadius(originals.borderRadius || '0');
    setFontSize(originals.fontSize || '');
    setFontWeight(originals.fontWeight || '400');
    setOpacity(originals.opacity ?? 100);
    setBoxShadow(originals.boxShadow || 'none');

    // Step 2: if non-default, request pseudo styles from iframe and overlay
    if (cssState === 'default' || !iframeRef?.current?.contentWindow) return;

    iframeRef.current.contentWindow.postMessage({ type: 'wf_get_pseudo_styles', pseudo: cssState }, '*');

    const handler = (e) => {
      if (e.data?.type !== 'wf_pseudo_styles' || e.data.pseudo !== cssState) return;
      const ps = e.data.styles || {};
      // _empty means no CSS rules exist for this pseudo-state → keep originals (already restored above)
      if (ps._empty) return;
      if (ps.color !== undefined) setTextColor(rgbToHex(ps.color) || ps.color);
      if (ps.backgroundColor !== undefined) setBgColor(rgbToHex(ps.backgroundColor) || ps.backgroundColor);
      if (ps.borderColor !== undefined) setBorderColor(rgbToHex(ps.borderColor) || ps.borderColor);
      if (ps.borderWidth !== undefined) setBorderWidth(matchBorderWidthPreset(parsePx(ps.borderWidth)));
      if (ps.borderRadius !== undefined) setBorderRadius(matchRadiusPreset(parsePx(ps.borderRadius)));
      if (ps.fontSize !== undefined) setFontSize(parsePx(ps.fontSize));
      if (ps.fontWeight !== undefined) setFontWeight(ps.fontWeight);
      if (ps.opacity !== undefined) setOpacity(Math.round(parseFloat(ps.opacity) * 100));
      if (ps.boxShadow !== undefined) setBoxShadow(matchShadowPreset(ps.boxShadow));
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [cssState, originals, iframeRef]);

  const sendStyleToIframe = useCallback((styles) => {
    if (!iframeRef?.current?.contentWindow) return;
    if (cssState === 'default') {
      iframeRef.current.contentWindow.postMessage({ type: 'wf_apply_style', styles }, '*');
    } else {
      iframeRef.current.contentWindow.postMessage({ type: 'wf_apply_pseudo_style', pseudo: cssState, styles }, '*');
    }
  }, [iframeRef, cssState]);

  const stateQualifier = cssState !== 'default' ? ` on ${cssState}` : '';

  // Change handlers
  const handleFontSizeChange = (v) => { setFontSize(v); sendStyleToIframe({ fontSize: v + 'px' }); };
  const handleFontWeightChange = (v) => { setFontWeight(v); sendStyleToIframe({ fontWeight: v }); };
  const handleFontStyleChange = (v) => { setFontStyle(v); sendStyleToIframe({ fontStyle: v }); };
  const handleTextAlignChange = (v) => { setTextAlign(v); sendStyleToIframe({ textAlign: v }); };
  const handleTextColorChange = (v) => { setTextColor(v); sendStyleToIframe({ color: v }); };
  const handleBgColorChange = (v) => {
    setBgColor(v);
    const isGrad = v && (v.startsWith('linear-gradient') || v.startsWith('radial-gradient'));
    sendStyleToIframe(isGrad ? { background: v } : { backgroundColor: v });
  };

  const handleBorderRadiusChange = (v) => {
    setBorderRadius(v);
    sendStyleToIframe({ borderRadius: v + 'px' });
  };

  const handleOpacityChange = (v) => {
    const num = parseInt(v, 10);
    setOpacity(num);
    sendStyleToIframe({ opacity: String(num / 100) });
  };

  const handleShadowChange = (cssVal) => {
    setBoxShadow(cssVal);
    sendStyleToIframe({ boxShadow: cssVal });
  };

  const handleBorderWidthChange = (v) => {
    setBorderWidth(v);
    sendStyleToIframe({ borderWidth: v + 'px' });
  };

  const handlePaddingChange = (side, v) => {
    const map = { top: setPaddingTop, right: setPaddingRight, bottom: setPaddingBottom, left: setPaddingLeft };
    map[side](v);
    sendStyleToIframe({ [`padding${side.charAt(0).toUpperCase() + side.slice(1)}`]: v + 'px' });
  };

  const handleMarginChange = (side, v) => {
    const map = { top: setMarginTop, right: setMarginRight, bottom: setMarginBottom, left: setMarginLeft };
    map[side](v);
    sendStyleToIframe({ [`margin${side.charAt(0).toUpperCase() + side.slice(1)}`]: v + 'px' });
  };

  // Text extras handlers
  const handleLineHeightChange = (v) => { setLineHeight(v); sendStyleToIframe({ lineHeight: v }); };
  const handleLetterSpacingChange = (v) => { setLetterSpacing(v); sendStyleToIframe({ letterSpacing: v + 'px' }); };
  const handleTextTransformChange = (v) => { setTextTransform(v); sendStyleToIframe({ textTransform: v }); };
  const handleTextDecorationChange = (v) => { setTextDecoration(v); sendStyleToIframe({ textDecoration: v }); };

  // Sizing handlers
  const handleWidthChange = (v) => { setElWidth(v); sendStyleToIframe({ width: v ? v + 'px' : 'auto' }); };
  const handleHeightChange = (v) => { setElHeight(v); sendStyleToIframe({ height: v ? v + 'px' : 'auto' }); };
  const handleMinWidthChange = (v) => { setMinWidth(v); sendStyleToIframe({ minWidth: v ? v + 'px' : 'none' }); };
  const handleMaxWidthChange = (v) => { setMaxWidth(v); sendStyleToIframe({ maxWidth: v ? v + 'px' : 'none' }); };
  const handleMinHeightChange = (v) => { setMinHeight(v); sendStyleToIframe({ minHeight: v ? v + 'px' : 'none' }); };
  const handleMaxHeightChange = (v) => { setMaxHeight(v); sendStyleToIframe({ maxHeight: v ? v + 'px' : 'none' }); };

  // Layout handlers
  const handleDisplayChange = (v) => { setDisplay(v); sendStyleToIframe({ display: v }); };
  const handleFlexDirectionChange = (v) => { setFlexDirection(v); sendStyleToIframe({ flexDirection: v }); };
  const handleJustifyContentChange = (v) => { setJustifyContent(v); sendStyleToIframe({ justifyContent: v }); };
  const handleAlignItemsChange = (v) => { setAlignItems(v); sendStyleToIframe({ alignItems: v }); };
  const handleGapChange = (v) => { setGap(v); sendStyleToIframe({ gap: v ? v + 'px' : '0' }); };
  const handleFlexWrapChange = (v) => { setFlexWrap(v); sendStyleToIframe({ flexWrap: v }); };

  // Position handlers
  const handlePositionChange = (v) => { setPosition(v); sendStyleToIframe({ position: v }); };
  const handlePosTopChange = (v) => { setPosTop(v); sendStyleToIframe({ top: v !== '' ? v + 'px' : 'auto' }); };
  const handlePosLeftChange = (v) => { setPosLeft(v); sendStyleToIframe({ left: v !== '' ? v + 'px' : 'auto' }); };
  const handlePosRightChange = (v) => { setPosRight(v); sendStyleToIframe({ right: v !== '' ? v + 'px' : 'auto' }); };
  const handlePosBottomChange = (v) => { setPosBottom(v); sendStyleToIframe({ bottom: v !== '' ? v + 'px' : 'auto' }); };
  const handleZIndexChange = (v) => { setZIndex(v); sendStyleToIframe({ zIndex: v }); };

  // Advanced effects handlers
  const buildFilterString = (blur, brightness, contrast, grayscale) => {
    const parts = [];
    if (blur > 0) parts.push(`blur(${blur}px)`);
    if (brightness !== 100) parts.push(`brightness(${brightness / 100})`);
    if (contrast !== 100) parts.push(`contrast(${contrast / 100})`);
    if (grayscale > 0) parts.push(`grayscale(${grayscale / 100})`);
    return parts.length > 0 ? parts.join(' ') : 'none';
  };
  const handleFilterBlurChange = (v) => { const n = Number(v); setFilterBlur(n); sendStyleToIframe({ filter: buildFilterString(n, filterBrightness, filterContrast, filterGrayscale) }); };
  const handleFilterBrightnessChange = (v) => { const n = Number(v); setFilterBrightness(n); sendStyleToIframe({ filter: buildFilterString(filterBlur, n, filterContrast, filterGrayscale) }); };
  const handleFilterContrastChange = (v) => { const n = Number(v); setFilterContrast(n); sendStyleToIframe({ filter: buildFilterString(filterBlur, filterBrightness, n, filterGrayscale) }); };
  const handleFilterGrayscaleChange = (v) => { const n = Number(v); setFilterGrayscale(n); sendStyleToIframe({ filter: buildFilterString(filterBlur, filterBrightness, filterContrast, n) }); };
  const handleMixBlendModeChange = (v) => { setMixBlendMode(v); sendStyleToIframe({ mixBlendMode: v }); };
  const handleCursorChange = (v) => { setCursorStyle(v); sendStyleToIframe({ cursor: v }); };
  const handleOverflowChange = (v) => { setOverflow(v); sendStyleToIframe({ overflow: v }); };

  // Build AI change description
  const buildChangeDescription = useCallback(() => {
    const changes = [];
    const tag = selectedElement?.tagName || 'element';
    const text = (selectedElement?.textContent || '').substring(0, 40);
    const stateStr = cssState !== 'default' ? ` in the :${cssState} state` : '';

    const shadowLabel = SHADOW_PRESETS.find(p => p.value === boxShadow)?.label || boxShadow;
    const origShadowLabel = SHADOW_PRESETS.find(p => p.value === originals.boxShadow)?.label || originals.boxShadow;

    if (content !== originals.content) changes.push(`Change the text content to "${content}"`);
    if (String(fontSize) !== String(originals.fontSize)) changes.push(`font-size to ${fontSize}px${stateStr}`);
    if (fontWeight !== originals.fontWeight) changes.push(`font-weight to ${fontWeight}${stateStr}`);
    if (fontStyle !== originals.fontStyle) changes.push(`font-style to ${fontStyle}${stateStr}`);
    if (textAlign !== originals.textAlign) changes.push(`text-align to ${textAlign}${stateStr}`);
    if (textColor !== originals.textColor) changes.push(`text color to ${textColor}${stateStr}`);
    if (bgColor !== originals.bgColor) changes.push(`background-color to ${bgColor}${stateStr}`);
    if (String(paddingTop) !== String(originals.paddingTop) ||
        String(paddingRight) !== String(originals.paddingRight) ||
        String(paddingBottom) !== String(originals.paddingBottom) ||
        String(paddingLeft) !== String(originals.paddingLeft)) {
      changes.push(`padding to ${paddingTop}px ${paddingRight}px ${paddingBottom}px ${paddingLeft}px${stateStr}`);
    }
    if (String(marginTop) !== String(originals.marginTop) ||
        String(marginRight) !== String(originals.marginRight) ||
        String(marginBottom) !== String(originals.marginBottom) ||
        String(marginLeft) !== String(originals.marginLeft)) {
      changes.push(`margin to ${marginTop}px ${marginRight}px ${marginBottom}px ${marginLeft}px${stateStr}`);
    }
    if (borderRadius !== originals.borderRadius) changes.push(`border-radius to ${borderRadius}px${stateStr}`);
    if (borderWidth !== originals.borderWidth) changes.push(`border-width to ${borderWidth}px${stateStr}`);
    if (borderColor !== originals.borderColor) changes.push(`border-color to ${borderColor}${stateStr}`);
    if (borderStyle !== originals.borderStyle) changes.push(`border-style to ${borderStyle}${stateStr}`);
    if (String(opacity) !== String(originals.opacity)) changes.push(`opacity to ${opacity}%${stateStr}`);
    if (boxShadow !== originals.boxShadow) changes.push(`box-shadow to "${shadowLabel}" (${boxShadow})${stateStr}`);

    // Text extras
    if (String(lineHeight) !== String(originals.lineHeight)) changes.push(`line-height to ${lineHeight}`);
    if (String(letterSpacing) !== String(originals.letterSpacing)) changes.push(`letter-spacing to ${letterSpacing}px`);
    if (textTransform !== originals.textTransform) changes.push(`text-transform to ${textTransform}`);
    if (textDecoration !== originals.textDecoration) changes.push(`text-decoration to ${textDecoration}`);
    // Sizing
    if (String(elWidth) !== String(originals.elWidth)) changes.push(`width to ${elWidth}px`);
    if (String(elHeight) !== String(originals.elHeight)) changes.push(`height to ${elHeight}px`);
    if (String(minWidth) !== String(originals.minWidth)) changes.push(`min-width to ${minWidth}px`);
    if (String(maxWidth) !== String(originals.maxWidth)) changes.push(`max-width to ${maxWidth}px`);
    if (String(minHeight) !== String(originals.minHeight)) changes.push(`min-height to ${minHeight}px`);
    if (String(maxHeight) !== String(originals.maxHeight)) changes.push(`max-height to ${maxHeight}px`);
    // Layout
    if (display !== originals.display) changes.push(`display to ${display}`);
    if (display !== originals.display || flexDirection !== originals.flexDirection) changes.push(`flex-direction to ${flexDirection}`);
    if (justifyContent !== originals.justifyContent) changes.push(`justify-content to ${justifyContent}`);
    if (alignItems !== originals.alignItems) changes.push(`align-items to ${alignItems}`);
    if (String(gap) !== String(originals.gap)) changes.push(`gap to ${gap}px`);
    if (flexWrap !== originals.flexWrap) changes.push(`flex-wrap to ${flexWrap}`);
    // Position
    if (position !== originals.position) changes.push(`position to ${position}`);
    if (String(posTop) !== String(originals.posTop)) changes.push(`top to ${posTop}px`);
    if (String(posLeft) !== String(originals.posLeft)) changes.push(`left to ${posLeft}px`);
    if (String(posRight) !== String(originals.posRight)) changes.push(`right to ${posRight}px`);
    if (String(posBottom) !== String(originals.posBottom)) changes.push(`bottom to ${posBottom}px`);
    if (String(zIndex) !== String(originals.zIndex)) changes.push(`z-index to ${zIndex}`);
    // Advanced effects
    if (mixBlendMode !== originals.mixBlendMode) changes.push(`mix-blend-mode to ${mixBlendMode}`);
    if (cursor !== originals.cursor) changes.push(`cursor to ${cursor}`);
    if (overflow !== originals.overflow) changes.push(`overflow to ${overflow}`);

    if (changes.length === 0) return '';

    const stateNote = cssState !== 'default'
      ? `. IMPORTANT: Apply these changes ONLY to the :${cssState} CSS pseudo-class, NOT to the default state. Use a CSS rule like .class:${cssState} { ... }`
      : '';

    return `For the <${tag}> element "${text}": ${changes.join(', ')}${stateNote}`;
  }, [selectedElement, content, fontSize, fontWeight, fontStyle, textAlign, textColor, bgColor,
      paddingTop, paddingRight, paddingBottom, paddingLeft, marginTop, marginRight, marginBottom,
      marginLeft, borderRadius, borderWidth, borderColor, borderStyle, opacity, boxShadow,
      lineHeight, letterSpacing, textTransform, textDecoration,
      elWidth, elHeight, minWidth, maxWidth, minHeight, maxHeight,
      display, flexDirection, justifyContent, alignItems, gap, flexWrap,
      position, posTop, posLeft, posRight, posBottom, zIndex,
      mixBlendMode, cursor, overflow,
      originals, cssState]);

  const hasChanges = buildChangeDescription() !== '';

  // Build a style diff object for direct save (no AI)
  const buildStyleDiff = useCallback(() => {
    const diff = {};
    // Only include properties the user explicitly interacted with (Bug 4 fix).
    // This prevents stale originals from causing ALL-defaults-included serialization.
    const t = touchedProps;

    // Text content always included if changed (not a CSS property)
    if (content !== originals.content) diff.textContent = content;
    // Typography
    if (t.has('fontSize') && String(fontSize) !== String(originals.fontSize)) diff.fontSize = fontSize + 'px';
    if (t.has('fontWeight') && fontWeight !== originals.fontWeight) diff.fontWeight = fontWeight;
    if (t.has('fontStyle') && fontStyle !== originals.fontStyle) diff.fontStyle = fontStyle;
    if (t.has('textAlign') && textAlign !== originals.textAlign) diff.textAlign = textAlign;
    if (t.has('textColor') && textColor !== originals.textColor) diff.color = textColor;
    if (t.has('bgColor') && bgColor !== originals.bgColor) diff.backgroundColor = bgColor;
    // Borders
    if (t.has('borderRadius') && borderRadius !== originals.borderRadius) diff.borderRadius = borderRadius + 'px';
    if (t.has('borderWidth') && borderWidth !== originals.borderWidth) diff.borderWidth = borderWidth + 'px';
    if (t.has('borderColor') && borderColor !== originals.borderColor) diff.borderColor = borderColor;
    if (t.has('borderStyle') && borderStyle !== originals.borderStyle) diff.borderStyle = borderStyle;
    // Effects
    if (t.has('opacity') && String(opacity) !== String(originals.opacity)) diff.opacity = String(Number(opacity) / 100);
    if (t.has('boxShadow') && boxShadow !== originals.boxShadow) diff.boxShadow = boxShadow;
    // Spacing
    if (t.has('paddingTop') && String(paddingTop) !== String(originals.paddingTop)) diff.paddingTop = paddingTop + 'px';
    if (t.has('paddingRight') && String(paddingRight) !== String(originals.paddingRight)) diff.paddingRight = paddingRight + 'px';
    if (t.has('paddingBottom') && String(paddingBottom) !== String(originals.paddingBottom)) diff.paddingBottom = paddingBottom + 'px';
    if (t.has('paddingLeft') && String(paddingLeft) !== String(originals.paddingLeft)) diff.paddingLeft = paddingLeft + 'px';
    if (t.has('marginTop') && String(marginTop) !== String(originals.marginTop)) diff.marginTop = marginTop + 'px';
    if (t.has('marginRight') && String(marginRight) !== String(originals.marginRight)) diff.marginRight = marginRight + 'px';
    if (t.has('marginBottom') && String(marginBottom) !== String(originals.marginBottom)) diff.marginBottom = marginBottom + 'px';
    if (t.has('marginLeft') && String(marginLeft) !== String(originals.marginLeft)) diff.marginLeft = marginLeft + 'px';
    // Text extras
    if (t.has('lineHeight') && String(lineHeight) !== String(originals.lineHeight)) diff.lineHeight = String(lineHeight);
    if (t.has('letterSpacing') && String(letterSpacing) !== String(originals.letterSpacing)) diff.letterSpacing = letterSpacing + 'px';
    if (t.has('textTransform') && textTransform !== originals.textTransform) diff.textTransform = textTransform;
    if (t.has('textDecoration') && textDecoration !== originals.textDecoration) diff.textDecoration = textDecoration;
    // Sizing
    if (t.has('elWidth') && String(elWidth) !== String(originals.elWidth)) diff.width = elWidth ? elWidth + 'px' : 'auto';
    if (t.has('elHeight') && String(elHeight) !== String(originals.elHeight)) diff.height = elHeight ? elHeight + 'px' : 'auto';
    if (t.has('minWidth') && String(minWidth) !== String(originals.minWidth)) diff.minWidth = minWidth ? minWidth + 'px' : '0';
    if (t.has('maxWidth') && String(maxWidth) !== String(originals.maxWidth)) diff.maxWidth = maxWidth ? maxWidth + 'px' : 'none';
    if (t.has('minHeight') && String(minHeight) !== String(originals.minHeight)) diff.minHeight = minHeight ? minHeight + 'px' : '0';
    if (t.has('maxHeight') && String(maxHeight) !== String(originals.maxHeight)) diff.maxHeight = maxHeight ? maxHeight + 'px' : 'none';
    // Layout
    if (t.has('display') && display !== originals.display) diff.display = display;
    if (t.has('flexDirection') && flexDirection !== originals.flexDirection) diff.flexDirection = flexDirection;
    if (t.has('justifyContent') && justifyContent !== originals.justifyContent) diff.justifyContent = justifyContent;
    if (t.has('alignItems') && alignItems !== originals.alignItems) diff.alignItems = alignItems;
    if (t.has('gap') && String(gap) !== String(originals.gap)) diff.gap = gap ? gap + 'px' : '0';
    if (t.has('flexWrap') && flexWrap !== originals.flexWrap) diff.flexWrap = flexWrap;
    // Position
    if (t.has('position') && position !== originals.position) diff.position = position;
    if (t.has('posTop') && String(posTop) !== String(originals.posTop)) diff.top = posTop ? posTop + 'px' : 'auto';
    if (t.has('posLeft') && String(posLeft) !== String(originals.posLeft)) diff.left = posLeft ? posLeft + 'px' : 'auto';
    if (t.has('posRight') && String(posRight) !== String(originals.posRight)) diff.right = posRight ? posRight + 'px' : 'auto';
    if (t.has('posBottom') && String(posBottom) !== String(originals.posBottom)) diff.bottom = posBottom ? posBottom + 'px' : 'auto';
    if (t.has('zIndex') && String(zIndex) !== String(originals.zIndex)) diff.zIndex = String(zIndex);
    // Advanced effects
    if (t.has('mixBlendMode') && mixBlendMode !== originals.mixBlendMode) diff.mixBlendMode = mixBlendMode;
    if (t.has('cursor') && cursor !== originals.cursor) diff.cursor = cursor;
    if (t.has('overflow') && overflow !== originals.overflow) diff.overflow = overflow;
    return Object.keys(diff).length > 0 ? diff : null;
  }, [content, fontSize, fontWeight, fontStyle, textAlign, textColor, bgColor,
      borderRadius, borderWidth, borderColor, borderStyle, opacity, boxShadow,
      paddingTop, paddingRight, paddingBottom, paddingLeft,
      marginTop, marginRight, marginBottom, marginLeft,
      lineHeight, letterSpacing, textTransform, textDecoration,
      elWidth, elHeight, minWidth, maxWidth, minHeight, maxHeight,
      display, flexDirection, justifyContent, alignItems, gap, flexWrap,
      position, posTop, posLeft, posRight, posBottom, zIndex,
      mixBlendMode, cursor, overflow,
      originals, touchedProps]);

  const buildStyleDiffRef = useRef(buildStyleDiff);
  const hasChangesRef = useRef(false);
  buildStyleDiffRef.current = buildStyleDiff;
  hasChangesRef.current = hasChanges;

  // Auto-save pending visual changes when panel closes (unmounts)
  // Uses direct style injection (no AI) to avoid content mangling
  useEffect(() => {
    return () => {
      if (!hasChangesRef.current) return;
      const diff = buildStyleDiffRef.current();
      if (!diff) return;
      const cssPath = selectedElement?.cssPath;
      if (!cssPath) return;

      // Fire-and-forget: save via direct style endpoint (no AI)
      const token = localStorage.getItem('orbis_token');
      axios.post(`${API_URL}/magic/projects/${projectId}/element/style`, {
        page_slug: currentPageSlug || 'home',
        css_path: cssPath,
        styles: diff,
      }, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state); })
        .catch(err => console.warn('[VisualEditPanel] Auto-save failed:', err.message));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    const desc = editRequest.trim() || buildChangeDescription();
    if (desc) onApplyEdit(desc);
  };

  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  // Element CRUD handlers (no AI)
  const [isCrudBusy, setIsCrudBusy] = useState(false);
  const authHeader = { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` };
  const pageSlug = currentPageSlug || 'home';
  const cssPath = selectedElement?.cssPath || '';

  const handleDelete = async () => {
    if (!cssPath || isCrudBusy) return;
    if (!window.confirm('Delete this element? This can be undone.')) return;
    setIsCrudBusy(true);
    try {
      const res = await axios.post(`${API_URL}/magic/projects/${projectId}/element/delete`,
        { page_slug: pageSlug, css_path: cssPath }, { headers: authHeader });
      if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state);
      onClose();
      toast.success('Element deleted');
    } catch (err) {
      toast.error('Delete failed');
    } finally {
      setIsCrudBusy(false);
    }
  };

  const handleDuplicate = async () => {
    if (!cssPath || isCrudBusy) return;
    setIsCrudBusy(true);
    try {
      const res = await axios.post(`${API_URL}/magic/projects/${projectId}/element/duplicate`,
        { page_slug: pageSlug, css_path: cssPath }, { headers: authHeader });
      if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state);
      toast.success('Element duplicated');
    } catch (err) {
      toast.error('Duplicate failed');
    } finally {
      setIsCrudBusy(false);
    }
  };

  const handleWrap = async () => {
    if (!cssPath || isCrudBusy) return;
    setIsCrudBusy(true);
    try {
      const res = await axios.post(`${API_URL}/magic/projects/${projectId}/element/wrap`,
        { page_slug: pageSlug, css_path: cssPath, wrapper_tag: 'div', wrapper_class: 'wrapper' },
        { headers: authHeader });
      if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state);
      toast.success('Wrapped in <div>');
    } catch (err) {
      toast.error('Wrap failed');
    } finally {
      setIsCrudBusy(false);
    }
  };

  // Image replacement handlers
  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUri = ev.target.result;
      setNewImageSrc(dataUri);
      setNewImagePreview(dataUri);
      if (iframeRef?.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({ type: 'wf_replace_image', src: dataUri }, '*');
      }
    };
    reader.readAsDataURL(file);
  };

  const handleImageUrlChange = (url) => {
    setNewImageSrc(url);
    setNewImagePreview(url);
    if (iframeRef?.current?.contentWindow) {
      iframeRef.current.contentWindow.postMessage({ type: 'wf_replace_image', src: url }, '*');
    }
  };

  const handleApplyImage = () => {
    if (!newImageSrc) return;
    onApplyEdit(`Replace the img element's src attribute with: ${newImageSrc}`);
  };

  if (!selectedElement) return null;

  const isInteractive = ['a', 'button', 'input', 'select', 'textarea'].includes(selectedElement.tagName) ||
    (selectedElement.classList || '').match(/\b(btn|button|cta|link|nav-link|tab)\b/i);

  // Find the current shadow label for the select value
  const shadowSelectValue = SHADOW_PRESETS.find(p => p.value === boxShadow)?.value || 'none';

  return (
    <div className="w-[280px] shrink-0 bg-[var(--orbis-bg)] border-r border-[rgba(255,255,255,0.07)] flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[rgba(255,255,255,0.07)]">
        <div className="flex items-center gap-2 min-w-0">
          <Paintbrush className="w-4 h-4 text-[var(--orbis-amber)] shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-normal text-[var(--orbis-t1)] truncate">Visual edits</p>
            <p className="text-[10px] text-[var(--orbis-t4)] truncate">
              &lt;{selectedElement.tagName}&gt;
              {selectedElement.classList ? ` .${selectedElement.classList.split(' ')[0]}` : ''}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={handleDuplicate} disabled={isCrudBusy} title="Duplicate element"
            className="p-1.5 rounded text-[var(--orbis-t4)] hover:text-[var(--orbis-t1)] hover:bg-[var(--orbis-s2)] transition-colors disabled:opacity-40">
            <Copy className="w-3.5 h-3.5" />
          </button>
          <button onClick={handleWrap} disabled={isCrudBusy} title="Wrap in <div>"
            className="p-1.5 rounded text-[var(--orbis-t4)] hover:text-[var(--orbis-t1)] hover:bg-[var(--orbis-s2)] transition-colors disabled:opacity-40">
            <Box className="w-3.5 h-3.5" />
          </button>
          <button onClick={handleDelete} disabled={isCrudBusy} title="Delete element"
            className="p-1.5 rounded text-[var(--orbis-t4)] hover:text-red-400 hover:bg-red-900/20 transition-colors disabled:opacity-40">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          <button onClick={onClose} className="p-1.5 rounded text-[var(--orbis-t4)] hover:text-[var(--orbis-t1)] transition-colors ml-1">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* CSS State Selector */}
      {isInteractive && (
        <div className="px-4 py-2 border-b border-[rgba(255,255,255,0.07)]">
          <label className="text-[11px] text-[var(--orbis-t4)] block mb-1">Apply styles to</label>
          <div className="flex items-center gap-1 bg-[var(--orbis-s1)] rounded-lg p-0.5 border border-[rgba(255,255,255,0.07)]">
            {CSS_STATES.map(s => (
              <button
                key={s.value}
                onClick={() => setCssState(s.value)}
                className={`flex-1 px-2 py-1 rounded-md text-[10px] font-medium transition-all ${
                  cssState === s.value
                    ? 'bg-[var(--orbis-amber)] text-[var(--orbis-bg)]'
                    : 'text-[var(--orbis-t4)] hover:text-[var(--orbis-t3)]'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          {cssState !== 'default' && (
            <p className="text-[10px] text-[var(--orbis-amber)]/70 mt-1">
              Changes will apply to the :{cssState} CSS state only
            </p>
          )}
        </div>
      )}

      {/* Scrollable sections */}
      <div className="flex-1 overflow-y-auto">

        {/* Image section — only for <img> elements */}
        {selectedElement.tagName === 'img' && (
          <Section title="Image">
            {/* Current image preview */}
            {(newImagePreview || selectedElement.src) && (
              <div className="w-full h-24 rounded-md overflow-hidden bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] flex items-center justify-center">
                <img
                  src={newImagePreview || selectedElement.src}
                  alt="preview"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            )}
            {/* Current src (truncated) */}
            {selectedElement.src && (
              <p className="text-[10px] text-[var(--orbis-t4)] truncate" title={selectedElement.src}>
                {selectedElement.src}
              </p>
            )}
            {/* Upload */}
            <div>
              <label className="text-[11px] text-[var(--orbis-t4)] block mb-1">Replace with file</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageFileChange}
                className="hidden"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] border-dashed rounded-md py-2 text-xs text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)] hover:border-[rgba(255,255,255,0.12)] transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload image
              </button>
            </div>
            {/* URL input */}
            <div>
              <label className="text-[11px] text-[var(--orbis-t4)] block mb-1">Or paste URL</label>
              <div className="flex items-center gap-1.5">
                <Link className="w-3.5 h-3.5 text-[var(--orbis-t4)] shrink-0" />
                <input
                  type="text"
                  placeholder="https://..."
                  value={newImageSrc.startsWith('data:') ? '' : newImageSrc}
                  onChange={e => handleImageUrlChange(e.target.value)}
                  className="flex-1 bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none focus:border-[rgba(255,255,255,0.12)]"
                />
              </div>
            </div>
            {/* Apply button */}
            <Button
              size="sm"
              onClick={handleApplyImage}
              disabled={!newImageSrc || isApplying}
              className="w-full bg-[var(--orbis-amber)] hover:bg-[var(--orbis-amber)]/80 text-[var(--orbis-bg)] text-xs"
            >
              {isApplying ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Applying...</>
              ) : (
                <><ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Replace image</>
              )}
            </Button>
          </Section>
        )}

        {/* Link — for <a> elements or elements with href */}
        {selectedElement.href != null && (
          <Section title="Link">
            {/* Current href */}
            {selectedElement.href && (
              <p className="text-[10px] text-[var(--orbis-t4)] truncate" title={selectedElement.href}>
                Current: {selectedElement.href}
              </p>
            )}
            {/* URL input */}
            <div>
              <label className="text-[11px] text-[var(--orbis-t4)] block mb-1">Link URL</label>
              <div className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-[var(--orbis-t4)] shrink-0" />
                <input
                  type="text"
                  placeholder="https://calendly.com/..."
                  value={linkHref}
                  onChange={e => setLinkHref(e.target.value)}
                  className="flex-1 bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none focus:border-[var(--orbis-amber)]/50"
                />
              </div>
            </div>
            {/* Quick presets */}
            <div className="flex flex-wrap gap-1">
              {[
                { label: '#', value: '#', title: 'Anchor' },
                { label: '/', value: '/', title: 'Home' },
                { label: 'tel:', value: 'tel:', title: 'Phone' },
                { label: 'mailto:', value: 'mailto:', title: 'Email' },
              ].map(p => (
                <button
                  key={p.value}
                  onClick={() => setLinkHref(p.value)}
                  title={p.title}
                  className="px-2 py-0.5 text-[10px] rounded border border-[rgba(255,255,255,0.12)] text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)] hover:border-[var(--orbis-amber)]/30 transition-colors"
                >
                  {p.label}
                </button>
              ))}
            </div>
            {/* Open in new tab toggle */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={linkNewTab}
                onChange={e => setLinkNewTab(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-[rgba(255,255,255,0.12)] bg-[var(--orbis-s1)] text-[var(--orbis-amber)] focus:ring-[var(--orbis-amber)]"
              />
              <span className="text-xs text-[var(--orbis-t3)] flex items-center gap-1">
                <ExternalLink className="w-3 h-3" /> Open in new tab
              </span>
            </label>
            {/* Apply button */}
            <Button
              size="sm"
              onClick={() => onLinkChange && onLinkChange(linkHref, linkNewTab)}
              disabled={!linkHref || isApplying}
              className="w-full bg-[var(--orbis-amber)] hover:bg-[var(--orbis-amber)]/80 text-[var(--orbis-bg)] text-xs"
            >
              {isApplying ? (
                <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Applying...</>
              ) : (
                <><Link className="w-3.5 h-3.5 mr-1.5" /> Update link</>
              )}
            </Button>
          </Section>
        )}

        {/* Typography */}
        <Section title="Typography">
          <TextInput label="Content" value={content} onChange={setContent} placeholder="Element text..." />
          <div className="grid grid-cols-2 gap-2">
            <SelectInput label="Font size" value={fontSize} onChange={handleFontSizeChange}
              options={[10,11,12,13,14,16,18,20,24,28,32,36,40,48,56,64,72,96].map(s => ({ value: s, label: s + 'px' }))}
            />
            <SelectInput label="Weight" value={fontWeight} onChange={handleFontWeightChange}
              options={[
                { value: '100', label: 'Thin' },
                { value: '200', label: 'Extra Light' },
                { value: '300', label: 'Light' },
                { value: '400', label: 'Normal' },
                { value: '500', label: 'Medium' },
                { value: '600', label: 'Semi Bold' },
                { value: '700', label: 'Bold' },
                { value: '800', label: 'Extra Bold' },
                { value: '900', label: 'Black' },
              ]}
            />
          </div>
          <SelectInput label="Style" value={fontStyle} onChange={handleFontStyleChange}
            options={[
              { value: 'normal', label: 'Normal' },
              { value: 'italic', label: 'Italic' },
              { value: 'oblique', label: 'Oblique' },
            ]}
          />
          <div>
            <label className="text-[11px] text-[var(--orbis-t4)] block mb-1">Alignment</label>
            <div className="flex items-center gap-1 bg-[var(--orbis-s1)] rounded-lg p-0.5 border border-[rgba(255,255,255,0.07)]">
              {[
                { val: 'left', Icon: AlignLeft },
                { val: 'center', Icon: AlignCenter },
                { val: 'right', Icon: AlignRight },
                { val: 'justify', Icon: AlignJustify },
              ].map(({ val, Icon }) => (
                <button
                  key={val}
                  onClick={() => handleTextAlignChange(val)}
                  className={`flex-1 p-1.5 rounded-md transition-all ${
                    textAlign === val ? 'bg-[var(--orbis-s2)] text-[var(--orbis-t1)]' : 'text-[var(--orbis-t4)] hover:text-[var(--orbis-t3)]'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 mx-auto" />
                </button>
              ))}
            </div>
          </div>
        </Section>

        {/* Colors */}
        <Section title={`Colors${stateQualifier}`}>
          <ColorInput label={`Text color${stateQualifier}`} value={textColor} onChange={handleTextColorChange} />
          <ColorInput label={`Background color${stateQualifier}`} value={bgColor} onChange={handleBgColorChange} allowGradient />
        </Section>

        {/* Spacing */}
        <Section title="Spacing" defaultOpen={false}>
          <CompactSpacing
            label="Padding"
            top={paddingTop} right={paddingRight} bottom={paddingBottom} left={paddingLeft}
            onChange={handlePaddingChange}
          />
          <CompactSpacing
            label="Margin"
            top={marginTop} right={marginRight} bottom={marginBottom} left={marginLeft}
            onChange={handleMarginChange}
          />
        </Section>

        {/* Border */}
        <Section title="Border" defaultOpen={false}>
          <div className="grid grid-cols-2 gap-2">
            <SelectInput label="Width" value={borderWidth} onChange={handleBorderWidthChange}
              options={BORDER_WIDTH_PRESETS}
            />
            <SelectInput label="Style" value={borderStyle} onChange={setBorderStyle}
              options={[
                { value: 'none', label: 'None' },
                { value: 'solid', label: 'Solid' },
                { value: 'dashed', label: 'Dashed' },
                { value: 'dotted', label: 'Dotted' },
              ]}
            />
          </div>
          <ColorInput label="Border color" value={borderColor} onChange={setBorderColor} />
        </Section>

        {/* Effects */}
        <Section title="Effects" defaultOpen={false}>
          <SelectInput label="Border radius" value={borderRadius} onChange={handleBorderRadiusChange}
            options={RADIUS_PRESETS}
          />
          <SelectInput label="Shadow" value={shadowSelectValue} onChange={handleShadowChange}
            options={SHADOW_PRESETS}
          />
          <SelectInput label="Opacity" value={opacity} onChange={handleOpacityChange}
            options={OPACITY_PRESETS.map(o => ({ value: o.value, label: o.label }))}
          />
        </Section>

        {/* Text Extras */}
        <Section title="Text Extras" defaultOpen={false}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <FieldLabel>Line height</FieldLabel>
              <input type="number" value={lineHeight} onChange={e => handleLineHeightChange(e.target.value)}
                step="0.1" min="0" placeholder="1.5"
                className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none"
              />
            </div>
            <div>
              <FieldLabel>Letter spacing (px)</FieldLabel>
              <input type="number" value={letterSpacing} onChange={e => handleLetterSpacingChange(e.target.value)}
                step="0.5" placeholder="0"
                className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none"
              />
            </div>
          </div>
          <SelectInput label="Text transform" value={textTransform} onChange={handleTextTransformChange}
            options={[
              { value: 'none', label: 'None' },
              { value: 'uppercase', label: 'Uppercase' },
              { value: 'lowercase', label: 'Lowercase' },
              { value: 'capitalize', label: 'Capitalize' },
            ]}
          />
          <SelectInput label="Text decoration" value={textDecoration} onChange={handleTextDecorationChange}
            options={[
              { value: 'none', label: 'None' },
              { value: 'underline', label: 'Underline' },
              { value: 'line-through', label: 'Line-through' },
              { value: 'overline', label: 'Overline' },
            ]}
          />
        </Section>

        {/* Sizing */}
        <Section title="Sizing" defaultOpen={false}>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: 'Width (px)', val: elWidth, handler: handleWidthChange },
              { label: 'Height (px)', val: elHeight, handler: handleHeightChange },
              { label: 'Min-W (px)', val: minWidth, handler: handleMinWidthChange },
              { label: 'Max-W (px)', val: maxWidth, handler: handleMaxWidthChange },
              { label: 'Min-H (px)', val: minHeight, handler: handleMinHeightChange },
              { label: 'Max-H (px)', val: maxHeight, handler: handleMaxHeightChange },
            ].map(({ label, val, handler }) => (
              <div key={label}>
                <FieldLabel>{label}</FieldLabel>
                <input type="number" value={val} onChange={e => handler(e.target.value)}
                  placeholder="auto" min="0"
                  className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none"
                />
              </div>
            ))}
          </div>
        </Section>

        {/* Layout */}
        <Section title="Layout" defaultOpen={false}>
          <SelectInput label="Display" value={display} onChange={handleDisplayChange}
            options={[
              { value: '', label: 'Inherit' },
              { value: 'block', label: 'Block' },
              { value: 'flex', label: 'Flex' },
              { value: 'grid', label: 'Grid' },
              { value: 'inline', label: 'Inline' },
              { value: 'inline-flex', label: 'Inline Flex' },
              { value: 'inline-block', label: 'Inline Block' },
              { value: 'none', label: 'None (hidden)' },
            ]}
          />
          {(display === 'flex' || display === 'inline-flex') && (<>
            <div className="grid grid-cols-2 gap-2">
              <SelectInput label="Direction" value={flexDirection} onChange={handleFlexDirectionChange}
                options={[
                  { value: 'row', label: 'Row →' },
                  { value: 'row-reverse', label: 'Row ←' },
                  { value: 'column', label: 'Column ↓' },
                  { value: 'column-reverse', label: 'Column ↑' },
                ]}
              />
              <SelectInput label="Wrap" value={flexWrap} onChange={handleFlexWrapChange}
                options={[
                  { value: 'nowrap', label: 'No wrap' },
                  { value: 'wrap', label: 'Wrap' },
                  { value: 'wrap-reverse', label: 'Wrap rev.' },
                ]}
              />
            </div>
            <SelectInput label="Justify content" value={justifyContent} onChange={handleJustifyContentChange}
              options={[
                { value: 'flex-start', label: 'Start' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'End' },
                { value: 'space-between', label: 'Space between' },
                { value: 'space-around', label: 'Space around' },
                { value: 'space-evenly', label: 'Space evenly' },
              ]}
            />
            <SelectInput label="Align items" value={alignItems} onChange={handleAlignItemsChange}
              options={[
                { value: 'stretch', label: 'Stretch' },
                { value: 'flex-start', label: 'Start' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'End' },
                { value: 'baseline', label: 'Baseline' },
              ]}
            />
            <div>
              <FieldLabel>Gap (px)</FieldLabel>
              <input type="number" value={gap} onChange={e => handleGapChange(e.target.value)}
                placeholder="0" min="0"
                className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none"
              />
            </div>
          </>)}
        </Section>

        {/* Position */}
        <Section title="Position" defaultOpen={false}>
          <SelectInput label="Position" value={position} onChange={handlePositionChange}
            options={[
              { value: 'static', label: 'Static (default)' },
              { value: 'relative', label: 'Relative' },
              { value: 'absolute', label: 'Absolute' },
              { value: 'fixed', label: 'Fixed' },
              { value: 'sticky', label: 'Sticky' },
            ]}
          />
          {position !== 'static' && (
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Top', val: posTop, handler: handlePosTopChange },
                { label: 'Left', val: posLeft, handler: handlePosLeftChange },
                { label: 'Right', val: posRight, handler: handlePosRightChange },
                { label: 'Bottom', val: posBottom, handler: handlePosBottomChange },
              ].map(({ label, val, handler }) => (
                <div key={label}>
                  <FieldLabel>{label} (px)</FieldLabel>
                  <input type="number" value={val} onChange={e => handler(e.target.value)}
                    placeholder="auto"
                    className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none"
                  />
                </div>
              ))}
            </div>
          )}
          <div>
            <FieldLabel>Z-index</FieldLabel>
            <input type="number" value={zIndex} onChange={e => handleZIndexChange(e.target.value)}
              placeholder="auto"
              className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none"
            />
          </div>
        </Section>

        {/* Advanced Effects */}
        <Section title="Animation" defaultOpen={false}>
          <AnimationPanel
            selectedElement={selectedElement}
            projectId={projectId}
            currentPageSlug={currentPageSlug}
            reviewEditorState={reviewEditorState}
            onApplied={onAnimationApplied}
          />
        </Section>

        <Section title="Advanced Effects" defaultOpen={false}>
          <div>
            <FieldLabel>Blur: {filterBlur}px</FieldLabel>
            <input type="range" min="0" max="20" step="0.5" value={filterBlur}
              onChange={e => handleFilterBlurChange(e.target.value)}
              className="w-full accent-[var(--orbis-amber)]"
            />
          </div>
          <div>
            <FieldLabel>Brightness: {filterBrightness}%</FieldLabel>
            <input type="range" min="0" max="200" step="5" value={filterBrightness}
              onChange={e => handleFilterBrightnessChange(e.target.value)}
              className="w-full accent-[var(--orbis-amber)]"
            />
          </div>
          <div>
            <FieldLabel>Contrast: {filterContrast}%</FieldLabel>
            <input type="range" min="0" max="200" step="5" value={filterContrast}
              onChange={e => handleFilterContrastChange(e.target.value)}
              className="w-full accent-[var(--orbis-amber)]"
            />
          </div>
          <div>
            <FieldLabel>Grayscale: {filterGrayscale}%</FieldLabel>
            <input type="range" min="0" max="100" step="5" value={filterGrayscale}
              onChange={e => handleFilterGrayscaleChange(e.target.value)}
              className="w-full accent-[var(--orbis-amber)]"
            />
          </div>
          <SelectInput label="Mix blend mode" value={mixBlendMode} onChange={handleMixBlendModeChange}
            options={[
              { value: 'normal', label: 'Normal' },
              { value: 'multiply', label: 'Multiply' },
              { value: 'screen', label: 'Screen' },
              { value: 'overlay', label: 'Overlay' },
              { value: 'darken', label: 'Darken' },
              { value: 'lighten', label: 'Lighten' },
              { value: 'color-dodge', label: 'Color dodge' },
              { value: 'color-burn', label: 'Color burn' },
              { value: 'hard-light', label: 'Hard light' },
              { value: 'soft-light', label: 'Soft light' },
              { value: 'difference', label: 'Difference' },
              { value: 'exclusion', label: 'Exclusion' },
            ]}
          />
          <SelectInput label="Overflow" value={overflow} onChange={handleOverflowChange}
            options={[
              { value: 'visible', label: 'Visible' },
              { value: 'hidden', label: 'Hidden' },
              { value: 'scroll', label: 'Scroll' },
              { value: 'auto', label: 'Auto' },
              { value: 'clip', label: 'Clip' },
            ]}
          />
          <SelectInput label="Cursor" value={cursor} onChange={handleCursorChange}
            options={[
              { value: 'auto', label: 'Auto' },
              { value: 'default', label: 'Default' },
              { value: 'pointer', label: 'Pointer' },
              { value: 'crosshair', label: 'Crosshair' },
              { value: 'move', label: 'Move' },
              { value: 'text', label: 'Text' },
              { value: 'not-allowed', label: 'Not allowed' },
              { value: 'grab', label: 'Grab' },
              { value: 'zoom-in', label: 'Zoom in' },
              { value: 'zoom-out', label: 'Zoom out' },
            ]}
          />
        </Section>
      </div>

      {/* Bottom action bar */}
      <div className="border-t border-[rgba(255,255,255,0.07)] p-3 space-y-2">
        <input
          type="text"
          placeholder="Or describe a change..."
          value={editRequest}
          onChange={e => setEditRequest(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-full bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-md px-2.5 py-1.5 text-xs text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none focus:border-[rgba(255,255,255,0.12)]"
        />
        <Button
          size="sm"
          onClick={handleApply}
          disabled={(!hasChanges && !editRequest.trim()) || isApplying}
          className="w-full bg-[var(--orbis-amber)] hover:bg-[var(--orbis-amber)]/80 text-[var(--orbis-bg)] text-xs"
        >
          {isApplying ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Applying...</>
          ) : (
            <><Send className="w-3.5 h-3.5 mr-1.5" /> Apply with AI</>
          )}
        </Button>
      </div>
    </div>
  );
}
